/**
 * Admin service — stateless orchestration for the admin control panel.
 *
 * The panel's screens call this; they never touch `adminApi` or `apiClient`
 * directly. Every list call answers a fully mapped page, and a response that
 * arrived but carried nothing usable raises `UnusableResponseError` so a screen
 * shows its error state instead of presenting a failure as "nothing here yet".
 */
import { UnusableResponseError } from '../../utils/apiErrorHandler';
import {
  withFeedTimezoneOffset,
  type FeedTimezonePayload,
} from '../feed/feedRequestPayload';
import adminApi from './adminApi';
import { createAdminRequestError } from './adminErrors';
import ADMIN_ENDPOINTS from './adminEndpoints';
import {
  MODERATION_STATUS,
  type ModerationCategory,
  type ModerationReviewAction,
  type ModerationSeverity,
  type ModerationStatus,
  type ModerationSurface,
  type ModerationTargetType,
} from '../moderation';
import {
  extractReportedContentData,
  isPagedEnvelope,
  mapAdminPage,
  mapAdminUser,
  mapDashboard,
  mapModerationQueue,
  mapModerationReviewOutcome,
  mapModerationSettings,
  mapModerationUsage,
  mapReportedContent,
  mapToggledModerationSetting,
} from './adminMapper';
import type {
  AdminAccountCreateRequest,
  AdminAccountUpdateRequest,
  AdminPage,
  AdminReportedPostsFetchRequest,
  AdminUser,
  AdminUserStatisticsListRequest,
  AdminUserStatisticsResponse,
  AdminUserUpdateRequest,
  ContentModerationReviewQueueRequest,
  DashboardNew,
  ErrorLogResponse,
  ModerationQueuePage,
  ModerationReviewOutcome,
  ModerationSetting,
  ModerationSettingsOverview,
  ModerationUsage,
  ReportedContentItem,
  ReportedContentPage,
} from './adminTypes';

const DEFAULT_PAGE_SIZE = 20;

/**
 * The moderation endpoints answer 200 with `{ status, statusCode, message }`
 * even when the call was refused, so `apiClient` cannot be the only guard:
 * without this the queue reports "Reports upheld." over a batch the server
 * rejected and then reloads the same rows unchanged.
 */
const assertMutationAccepted = (data: unknown, endpoint: string): void => {
  if (!data || typeof data !== 'object') {
    throw new UnusableResponseError(endpoint);
  }
  const body = data as {
    status?: unknown;
    statusCode?: unknown;
    message?: unknown;
  };
  const statusCode = typeof body.statusCode === 'number' ? body.statusCode : 0;
  const refused =
    statusCode >= 400 ||
    (typeof body.status === 'string' && body.status.toLowerCase() !== 'success');

  if (refused) {
    throw createAdminRequestError(
      typeof body.message === 'string' ? body.message : null,
      statusCode >= 400 ? statusCode : undefined,
    );
  }
};

/**
 * `withFeedTimezoneOffset` only accepts payloads that declare the offset, so the
 * caller-supplied half of the request is spelled out separately and the helper
 * fills the offset in.
 */
type ReportedPostsPayload = Omit<
  AdminReportedPostsFetchRequest,
  'timezoneOffset'
> &
  FeedTimezonePayload;

type ModerationQueuePayload = Omit<
  ContentModerationReviewQueueRequest,
  'timezoneOffset'
> &
  FeedTimezonePayload;

export interface AdminUserListParams {
  userName?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  isActive?: boolean | null;
  isUserBlocked?: boolean | null;
  isEmailVerified?: boolean | null;
  pageNumber?: number;
  pageSize?: number;
}

export interface AdminAccountListParams {
  searchTerm?: string;
  isActive?: boolean | null;
  pageNumber?: number;
  pageSize?: number;
}

export interface ErrorLogListParams {
  pageNumber?: number;
  pageSize?: number;
  startDate?: string;
  endDate?: string;
  type?: string;
  searchKeyword?: string;
}

export interface ReportedContentParams {
  /** Signed-in admin's user id — the endpoint records who fetched the queue. */
  userId: number;
  startIndex?: number;
  pageSize?: number;
  isReviewed?: boolean | null;
}

export interface DashboardMetricsParams {
  startDate: string;
  endDate: string;
}

/**
 * Screening-queue filters. Every value is a union member rather than free text:
 * the procedure coerces an out-of-range filter instead of refusing it, so a
 * typo would quietly answer with a different page than the one asked for.
 */
export interface ModerationQueueParams {
  startIndex?: number;
  pageSize?: number;
  /** Defaults to `HeldForReview` — the backlog an operator opens the queue for. */
  moderationStatus?: ModerationStatus;
  surface?: ModerationSurface | null;
  severity?: ModerationSeverity | null;
  category?: ModerationCategory | null;
}

export interface ModerationReviewParams {
  targetType: ModerationTargetType;
  targetId: number;
  reviewAction: ModerationReviewAction;
  reviewerNote?: string;
  /**
   * Allow-list entries cannot be removed through any endpoint today, so a
   * screen must treat this as irreversible.
   */
  addToAllowList?: boolean;
}

export interface ModerationSettingToggleParams {
  settingKey: string;
  isActive: boolean;
  /** Only for the keys that carry a value (a cap, a threshold). */
  settingValue?: string | null;
}

export interface ReviewReportedContentParams {
  isReportApproved: boolean;
  reviewerComments?: string;
  items: readonly Pick<
    ReportedContentItem,
    'reportedPostId' | 'postId' | 'commentId'
  >[];
}

/** Empty string / null filters must be omitted, not sent as null. */
const optional = <T>(value: T | null | undefined): T | null =>
  value === undefined || value === '' ? null : (value as T | null);

const adminService = {
  // ─── Users ─────────────────────────────────────────────────────────────────

  getUsers: async (
    params: AdminUserListParams = {},
  ): Promise<AdminPage<AdminUser>> => {
    const pageNumber = params.pageNumber ?? 1;
    const pageSize = params.pageSize ?? DEFAULT_PAGE_SIZE;
    const { data } = await adminApi.listUsers({
      userName: optional(params.userName),
      firstName: optional(params.firstName),
      lastName: optional(params.lastName),
      email: optional(params.email),
      isActive: optional(params.isActive),
      isUserBlocked: optional(params.isUserBlocked),
      isEmailVerified: optional(params.isEmailVerified),
      pageNumber,
      pageSize,
    });

    if (!isPagedEnvelope(data, 'items')) {
      throw new UnusableResponseError(ADMIN_ENDPOINTS.USER.LIST);
    }
    return mapAdminPage(data, mapAdminUser, { pageNumber, pageSize });
  },

  getUser: async (userGuid: string): Promise<AdminUser> => {
    const { data } = await adminApi.getUser(userGuid);
    const user = data?.userInfo;
    if (!user?.userGUID) {
      throw new UnusableResponseError(ADMIN_ENDPOINTS.USER.BY_GUID(userGuid));
    }
    return mapAdminUser(user);
  },

  updateUser: async (
    userGuid: string,
    payload: AdminUserUpdateRequest,
  ): Promise<AdminUser | null> => {
    const { data } = await adminApi.updateUser(userGuid, payload);
    return data?.user ? mapAdminUser(data.user) : null;
  },

  setUserActiveStatus: async (
    userGuid: string,
    isActive: boolean,
  ): Promise<AdminUser | null> => {
    const { data } = await adminApi.setUserActiveStatus({ userGUID: userGuid, isActive });
    return data?.user ? mapAdminUser(data.user) : null;
  },

  setTemporaryPassword: async (
    userGuid: string,
    newPassword: string,
  ): Promise<void> => {
    await adminApi.setTemporaryPassword({ userGuid, newPassword });
  },

  getUserStatistics: async (
    params: AdminUserStatisticsListRequest = {},
  ): Promise<{ items: AdminUserStatisticsResponse[]; totalCount: number }> => {
    const pageNumber = params.pageNumber ?? 1;
    const pageSize = params.pageSize ?? DEFAULT_PAGE_SIZE;
    const { data } = await adminApi.listUserStatistics({
      ...params,
      pageNumber,
      pageSize,
    });

    if (!isPagedEnvelope(data, 'users')) {
      throw new UnusableResponseError(ADMIN_ENDPOINTS.USER.STATISTICS);
    }
    return {
      items: Array.isArray(data.users) ? data.users : [],
      totalCount: data.totalCount ?? 0,
    };
  },

  // ─── Admin accounts ────────────────────────────────────────────────────────

  getAdminAccounts: async (
    params: AdminAccountListParams = {},
  ): Promise<AdminPage<AdminUser>> => {
    const pageNumber = params.pageNumber ?? 1;
    const pageSize = params.pageSize ?? DEFAULT_PAGE_SIZE;
    const { data } = await adminApi.listAdminAccounts({
      searchTerm: optional(params.searchTerm),
      isActive: optional(params.isActive),
      pageNumber,
      pageSize,
    });

    if (!isPagedEnvelope(data, 'items')) {
      throw new UnusableResponseError(ADMIN_ENDPOINTS.ACCOUNT.LIST);
    }
    return mapAdminPage(data, mapAdminUser, { pageNumber, pageSize });
  },

  createAdminAccount: async (
    payload: AdminAccountCreateRequest,
  ): Promise<AdminUser | null> => {
    const { data } = await adminApi.createAdminAccount(payload);
    return data?.user ? mapAdminUser(data.user) : null;
  },

  updateAdminAccount: async (
    userGuid: string,
    payload: AdminAccountUpdateRequest,
  ): Promise<AdminUser | null> => {
    const { data } = await adminApi.updateAdminAccount(userGuid, payload);
    return data?.user ? mapAdminUser(data.user) : null;
  },

  setAdminAccountActiveStatus: async (
    userGuid: string,
    isActive: boolean,
  ): Promise<AdminUser | null> => {
    const { data } = await adminApi.setAdminAccountActiveStatus({
      userGuid,
      isActive,
    });
    return data?.user ? mapAdminUser(data.user) : null;
  },

  // ─── Dashboard ─────────────────────────────────────────────────────────────

  getDashboardMetrics: async (
    params: DashboardMetricsParams,
  ): Promise<DashboardNew> => {
    const { data } = await adminApi.getDashboardMetrics(params);
    const dashboard = mapDashboard(data);
    if (!dashboard) {
      throw new UnusableResponseError(ADMIN_ENDPOINTS.DASHBOARD.METRICS);
    }
    return dashboard;
  },

  // ─── Content moderation ────────────────────────────────────────────────────

  getReportedContent: async (
    params: ReportedContentParams,
  ): Promise<ReportedContentPage> => {
    const payload: ReportedPostsPayload = {
      userId: params.userId,
      startIndex: params.startIndex ?? 0,
      pageSize: params.pageSize ?? DEFAULT_PAGE_SIZE,
      isReviewed: params.isReviewed ?? null,
    };
    const { data } = await adminApi.getReportedPosts(
      withFeedTimezoneOffset(payload),
    );
    const reported = extractReportedContentData(data);
    if (!reported) {
      throw new UnusableResponseError(ADMIN_ENDPOINTS.DASHBOARD.REPORTED_POSTS);
    }
    return mapReportedContent(reported);
  },

  reviewReportedContent: async (
    params: ReviewReportedContentParams,
  ): Promise<void> => {
    const { data } = await adminApi.bulkUpdateReportedContent({
      isReportApproved: params.isReportApproved,
      reviewerComments: params.reviewerComments ?? null,
      reportDetails: params.items.map(item => ({
        reportedPostID: item.reportedPostId,
        postId: item.postId,
        commentId: item.commentId,
      })),
    });
    assertMutationAccepted(data, ADMIN_ENDPOINTS.DASHBOARD.BULK_UPDATE_REPORTED);
  },

  setPostsVisibility: async (
    postIds: readonly number[],
    isActive: boolean,
  ): Promise<void> => {
    const { data } = await adminApi.bulkUpdatePostVisibility({
      postIds: [...postIds],
      isActive,
    });
    assertMutationAccepted(data, ADMIN_ENDPOINTS.DASHBOARD.BULK_UPDATE_POST);
  },

  // ─── Automated content moderation ──────────────────────────────────────────

  getModerationReviewQueue: async (
    params: ModerationQueueParams = {},
  ): Promise<ModerationQueuePage> => {
    const requested = {
      startIndex: params.startIndex ?? 0,
      pageSize: params.pageSize ?? DEFAULT_PAGE_SIZE,
    };
    const payload: ModerationQueuePayload = {
      ...requested,
      moderationStatus: params.moderationStatus ?? MODERATION_STATUS.heldForReview,
      surface: params.surface ?? null,
      severity: params.severity ?? null,
      category: params.category ?? null,
    };
    const { data } = await adminApi.getModerationReviewQueue(
      withFeedTimezoneOffset(payload),
    );
    const page = mapModerationQueue(data, requested);
    if (!page) {
      throw new UnusableResponseError(
        ADMIN_ENDPOINTS.CONTENT_MODERATION.REVIEW_QUEUE,
      );
    }
    return page;
  },

  /**
   * Applies a ruling. `reviewAction` is always sent explicitly — the API
   * rejects the ordinal 0 an omitted action would produce rather than guessing.
   *
   * A media target's ruling lands on the parent post, comment or message, so
   * the caller must re-read the queue afterwards instead of patching the row.
   */
  applyModerationReview: async (
    params: ModerationReviewParams,
  ): Promise<ModerationReviewOutcome> => {
    const { data } = await adminApi.applyModerationReview({
      targetType: params.targetType,
      targetId: params.targetId,
      reviewAction: params.reviewAction,
      reviewerNote: params.reviewerNote?.trim() ? params.reviewerNote.trim() : null,
      addToAllowList: params.addToAllowList ?? false,
    });
    assertMutationAccepted(data, ADMIN_ENDPOINTS.CONTENT_MODERATION.APPLY_REVIEW);
    const outcome = mapModerationReviewOutcome(data);
    if (!outcome) {
      throw new UnusableResponseError(
        ADMIN_ENDPOINTS.CONTENT_MODERATION.APPLY_REVIEW,
      );
    }
    if (!outcome.isSuccess) {
      throw createAdminRequestError(outcome.message || null);
    }
    return outcome;
  },

  getModerationSettings: async (): Promise<ModerationSettingsOverview> => {
    const { data } = await adminApi.getModerationSettings();
    const overview = mapModerationSettings(data);
    if (!overview) {
      throw new UnusableResponseError(ADMIN_ENDPOINTS.CONTENT_MODERATION.SETTINGS);
    }
    return overview;
  },

  /**
   * Flips one kill switch. The key set is fixed — the stored procedure is
   * update-only and refuses an unknown key — so screens must offer only the
   * keys `getModerationSettings` returned.
   */
  toggleModerationSetting: async (
    params: ModerationSettingToggleParams,
  ): Promise<ModerationSetting | null> => {
    const { data } = await adminApi.toggleModerationSetting({
      settingKey: params.settingKey,
      settingValue: params.settingValue ?? null,
      isActive: params.isActive,
    });
    assertMutationAccepted(
      data,
      ADMIN_ENDPOINTS.CONTENT_MODERATION.TOGGLE_SETTING,
    );
    return mapToggledModerationSetting(data);
  },

  getModerationUsage: async (asOfUtc?: string): Promise<ModerationUsage> => {
    const { data } = await adminApi.getModerationUsage(asOfUtc);
    const usage = mapModerationUsage(data);
    if (!usage) {
      throw new UnusableResponseError(ADMIN_ENDPOINTS.CONTENT_MODERATION.USAGE);
    }
    return usage;
  },

  // ─── Error log ─────────────────────────────────────────────────────────────

  getErrorLogs: async (
    params: ErrorLogListParams = {},
  ): Promise<AdminPage<ErrorLogResponse>> => {
    const pageNumber = params.pageNumber ?? 1;
    const pageSize = params.pageSize ?? DEFAULT_PAGE_SIZE;
    const { data } = await adminApi.listErrorLogs({
      pageNumber,
      pageSize,
      startDate: optional(params.startDate),
      endDate: optional(params.endDate),
      type: optional(params.type),
      searchKeyword: optional(params.searchKeyword),
    });

    if (!isPagedEnvelope(data, 'items')) {
      throw new UnusableResponseError(ADMIN_ENDPOINTS.ERROR_LOG.LIST);
    }
    return mapAdminPage(data, entry => entry, { pageNumber, pageSize });
  },
};

export default adminService;
